import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import {
  LayoutDashboard, Save, ToggleLeft, ToggleRight,
  BarChart3, Newspaper, Megaphone, TrendingUp,
  Activity, Package, Hash
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import api from '../services/api';
import usePageTitle from '../hooks/usePageTitle';

const BRAND_BLUE = '#2B4B6A';

const MODULE_DEFINITIONS = [
  {
    key: 'animated_counters',
    label: 'Animated Counters',
    description: 'Stat cards with count-up animation (Active Deals, Requests, Views, etc.)',
    icon: BarChart3,
    hasCount: false,
    hasSpeed: false,
  },
  {
    key: 'opportunity_ticker',
    label: 'Opportunity Ticker',
    description: 'Scrolling ticker bar showing live opportunities at the bottom of the dashboard',
    icon: TrendingUp,
    hasCount: false,
    hasSpeed: true,
    speedOptions: ['slow', 'normal', 'fast'],
  },
  {
    key: 'news_articles',
    label: 'News Articles',
    description: 'WordPress articles carousel with thumbnails and excerpts',
    icon: Newspaper,
    hasCount: true,
    countLabel: 'Number of articles',
    countMin: 1,
    countMax: 20,
  },
  {
    key: 'announcements',
    label: 'Announcements Panel',
    description: 'Latest announcements list on the dashboard',
    icon: Megaphone,
    hasCount: true,
    countLabel: 'Number of announcements',
    countMin: 1,
    countMax: 20,
  },
  {
    key: 'latest_opportunities',
    label: 'Opportunities List',
    description: 'Auto-scrolling list of opportunities (My Deals for sellers, Browse for buyers)',
    icon: Package,
    hasCount: true,
    countLabel: 'Number of opportunities',
    countMin: 1,
    countMax: 20,
  },
  {
    key: 'activity_feed',
    label: 'Activity Feed',
    description: 'Live platform activity feed (signups, LOIs, verifications from various countries)',
    icon: Activity,
    hasCount: false,
  },
  {
    key: 'commodity_tracker',
    label: 'Commodity / Minerals Tracker',
    description: 'Price tracker grid for commodities and minerals (placeholder data for now)',
    icon: Hash,
    hasCount: true,
    countLabel: 'Number of commodities to track',
    countMin: 2,
    countMax: 12,
  },
];

const AdminDashboardControl = () => {
  const [modules, setModules] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  usePageTitle('Dashboard Control');

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const res = await api.get('/admin/dashboard-control');
      setModules(res.data?.modules || {});
    } catch (error) {
      toast.error('Failed to load dashboard settings');
    } finally {
      setLoading(false);
    }
  };

  const toggleModule = (key) => {
    setModules(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        enabled: !(prev[key]?.enabled !== false),
      }
    }));
  };

  const updateModuleField = (key, field, value) => {
    setModules(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        [field]: value,
      }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/admin/dashboard-control', modules);
      toast.success('Dashboard settings saved');
    } catch (error) {
      toast.error('Failed to save dashboard settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Sidebar>
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-300 border-t-blue-600" />
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <div className="p-6 md:p-8 bg-gray-50/50 min-h-screen">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: `${BRAND_BLUE}15` }}
            >
              <LayoutDashboard className="w-5 h-5" style={{ color: BRAND_BLUE }} />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Dashboard Control</h1>
              <p className="text-sm text-gray-500">Enable, disable, and configure user dashboard modules</p>
            </div>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 text-white text-sm font-medium px-5 py-2.5 rounded-lg transition hover:shadow-md disabled:opacity-50"
            style={{ backgroundColor: BRAND_BLUE }}
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>

        {/* Module Cards */}
        <div className="space-y-4">
          {MODULE_DEFINITIONS.map((mod) => {
            const isEnabled = modules[mod.key]?.enabled !== false;
            const IconComp = mod.icon;

            return (
              <div
                key={mod.key}
                className={`rounded-xl border bg-white p-5 transition-all ${
                  isEnabled ? 'border-gray-200' : 'border-gray-100 opacity-60'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{
                        backgroundColor: isEnabled ? `${BRAND_BLUE}10` : '#F3F4F6',
                      }}
                    >
                      <IconComp
                        className="w-5 h-5"
                        style={{ color: isEnabled ? BRAND_BLUE : '#9CA3AF' }}
                      />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900">{mod.label}</h3>
                      <p className="text-xs text-gray-500 mt-0.5">{mod.description}</p>
                    </div>
                  </div>

                  {/* Toggle */}
                  <button
                    onClick={() => toggleModule(mod.key)}
                    className="flex-shrink-0"
                  >
                    {isEnabled ? (
                      <ToggleRight className="w-10 h-10 text-emerald-500" />
                    ) : (
                      <ToggleLeft className="w-10 h-10 text-gray-300" />
                    )}
                  </button>
                </div>

                {/* Config fields (only when enabled) */}
                {isEnabled && (mod.hasCount || mod.hasSpeed) && (
                  <div className="mt-4 pt-4 border-t border-gray-100 flex flex-wrap gap-6">
                    {mod.hasCount && (
                      <div className="flex items-center gap-3">
                        <label className="text-xs font-medium text-gray-600">
                          {mod.countLabel || 'Count'}
                        </label>
                        <input
                          type="number"
                          min={mod.countMin || 1}
                          max={mod.countMax || 20}
                          value={modules[mod.key]?.count || 5}
                          onChange={(e) => updateModuleField(mod.key, 'count', parseInt(e.target.value) || 5)}
                          className="w-20 px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    )}
                    {mod.hasSpeed && (
                      <div className="flex items-center gap-3">
                        <label className="text-xs font-medium text-gray-600">Speed</label>
                        <select
                          value={modules[mod.key]?.speed || 'normal'}
                          onChange={(e) => updateModuleField(mod.key, 'speed', e.target.value)}
                          className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="slow">Slow</option>
                          <option value="normal">Normal</option>
                          <option value="fast">Fast</option>
                        </select>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Info */}
        <div className="mt-6 rounded-xl bg-blue-50 border border-blue-100 p-4">
          <p className="text-xs text-blue-700">
            These settings control which modules appear on the user dashboard across all tenants.
            Disabled modules will be hidden from all users. Changes take effect immediately after saving.
          </p>
        </div>
      </div>
    </Sidebar>
  );
};

export default AdminDashboardControl;
